import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { Comment } from '../../../data-contract/comment';
import { useApplicationFetchers } from '../../../fetchers/use-application-fetchers';

const isNotFoundError = (error: Error): boolean =>
    'response' in error && error.response instanceof Response && error.response.status === 404;

type UseRepliesQueryParams = {
    readonly architectureId: string;
    readonly enabled?: boolean;
    readonly itemId: string;
    readonly parentCommentId: string;
    readonly version: number;
};

export const repliesQueryKey = ({
    architectureId,
    itemId,
    parentCommentId,
    version,
}: Omit<UseRepliesQueryParams, 'enabled'>) => ['replies', architectureId, itemId, version, parentCommentId] as const;

export const useRepliesQuery = ({
    architectureId,
    enabled = true,
    itemId,
    parentCommentId,
    version,
}: UseRepliesQueryParams): UseQueryResult<readonly Comment[], Error> => {
    const { fetchReplies } = useApplicationFetchers();

    return useQuery<readonly Comment[], Error>({
        enabled,
        queryFn: async () => {
            const result = await fetchReplies({ architectureId, commentId: parentCommentId, itemId });
            if (result.isOk) {
                return result.value;
            }
            if (isNotFoundError(result.error)) {
                return [];
            }
            throw result.error;
        },
        queryKey: repliesQueryKey({ architectureId, itemId, parentCommentId, version }),
        throwOnError: false,
    });
};
